import React, { useState, useEffect } from 'react'
import { Modal, Row, Col, Form } from 'react-bootstrap'
import { Button } from '@mantine/core'
import { toast } from 'react-toastify'
import {
  AttendanceRecord,
  CreateAttendanceFormData,
  UpdateAttendanceFormData,
  CRUDModalState,
  DEFAULT_CREATE_FORM
} from '../../types/AttendanceTypes'
import {
  createAttendanceRecord,
  updateAttendanceRecord,
  deleteAttendanceRecord,
  getAttendanceRecord
} from '../../../api/AttendanceAPI'
import InputErrorBox from '../../components/InputErrorBox'
import Select2 from '../../components/select2'
import { InputErrorType } from '../../types/OtherTypes'
import { useAuth } from '../../modules/auth'
import { getDate } from '../../../_metronic/helpers'
import { getGeneralConfig } from '../../../api/ConfigurationAPI'

interface AttendanceCRUDModalProps {
  modalState: CRUDModalState
  onClose: () => void
  onSuccess: () => void
  employees: Array<{ value: string; label: string }>
  onCreated?: (record: AttendanceRecord) => void
}

const AttendanceCRUDModal: React.FC<AttendanceCRUDModalProps> = ({
  modalState,
  onClose,
  onSuccess,
  employees,
  onCreated
}) => {
  const { currentUser } = useAuth()
  const { formattedDate } = getDate()
  const [loading, setLoading] = useState(false)
  const [inputError, setInputError] = useState<InputErrorType>({})
  const [formData, setFormData] = useState<CreateAttendanceFormData>(DEFAULT_CREATE_FORM)
  const [viewRecord, setViewRecord] = useState<AttendanceRecord | null>(null)
  // const [generalSettings, setGeneralSettings] = useState<{ inTime: string; outTime: string }>({
  //   inTime: '09:00',
  //   outTime: '19:00'
  // })
  const [shift, setShift] = useState<{ startTime: string; endTime: string } | null>(null);
  // useEffect(() => {
  //   // Fetch general settings when component mounts
  //   const fetchGeneralSettings = async () => {
  //     try {
  //       const response = await getGeneralConfig()
  //       if (response.statusCode === 200 && response.data && response.data.length > 0) {
  //         // const config = response.data[0]
  //         // setGeneralSettings({
  //         //   inTime: config.inTime || '09:00',
  //         //   outTime: config.outTime || '19:00'
  //         // })
  //         const [shift, setShift] = useState<{ startTime: string; endTime: string } | null>(null);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching general settings:', error)
  //       // Keep default values
  //     }
  //   }

  //   fetchGeneralSettings()
  // }, [])

  useEffect(() => {
    if (modalState.isOpen) {
      setInputError({})

      if (modalState.mode === 'create') {
        setFormData({
          ...DEFAULT_CREATE_FORM,
          date: formattedDate
        })
      } else if (modalState.mode === 'edit' && modalState.record) {
        // Parse the date properly - handle both ISO format and other formats
        let dateValue = modalState.record.date
        if (dateValue.includes('T')) {
          dateValue = dateValue.split('T')[0] // Extract YYYY-MM-DD from ISO format
        }
        // If date is in DD-MM-YYYY format, convert to YYYY-MM-DD
        if (dateValue.includes('-') && dateValue.length === 10) {
          const parts = dateValue.split('-')
          if (parts[0].length === 2) {
            // It's DD-MM-YYYY, convert to YYYY-MM-DD
            dateValue = `${parts[2]}-${parts[1]}-${parts[0]}`
          }
        }
        setFormData({
          employeeCode: modalState.record.employeeCode,
          date: dateValue,
          inTime: modalState.record.inTime || '',
          outTime: modalState.record.outTime || '',
          duration: modalState.record.duration || '',
          lateBy: modalState.record.lateBy || '',
          earlyBy: modalState.record.earlyBy || '',
          overtime: modalState.record.overtime || ''
        })
      } else if (modalState.mode === 'view' && modalState.record) {
        setViewRecord(modalState.record)
      }
    }
  }, [modalState, formattedDate])

  // Function to calculate duration between two times
  const calculateDuration = (inTime: string, outTime: string): string => {
    if (!inTime || !outTime) return ''

    try {
      const parseTime = (timeStr: string): Date => {
        const today = new Date()
        today.setSeconds(0, 0)
        const [hours, minutes] = timeStr.split(':').map(Number)
        today.setHours(hours, minutes || 0)
        return today
      }

      const startTime = parseTime(inTime)
      const endTime = parseTime(outTime)

      // Handle overnight shifts
      if (endTime < startTime) {
        endTime.setDate(endTime.getDate() + 1)
      }

      const diffMs = endTime.getTime() - startTime.getTime()
      const hours = Math.floor(diffMs / (1000 * 60 * 60))
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

      return `${hours}:${minutes.toString().padStart(2, '0')}`
    } catch (error) {
      console.error('Error calculating duration:', error)
      return ''
    }
  }

  // Function to calculate late by based on standard in time
  const calculateLateBy = (actualInTime: string, standardInTime: string = '09:00'): string => {
    if (!actualInTime) return ''

    try {
      const parseTime = (timeStr: string): Date => {
        const today = new Date()
        today.setSeconds(0, 0)
        const [hours, minutes] = timeStr.split(':').map(Number)
        today.setHours(hours, minutes || 0)
        return today
      }

      const actual = parseTime(actualInTime)
      const standard = parseTime(standardInTime)

      if (actual > standard) {
        const diffMs = actual.getTime() - standard.getTime()
        const hours = Math.floor(diffMs / (1000 * 60 * 60))
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

        if (hours > 0 || minutes > 0) {
          return `${hours}:${minutes.toString().padStart(2, '0')}`
        }
      }

      return ''
    } catch (error) {
      console.error('Error calculating late by:', error)
      return ''
    }
  }

  // Function to calculate early by based on standard out time
  const calculateEarlyBy = (actualOutTime: string, standardOutTime: string = '18:00'): string => {
    if (!actualOutTime) return ''

    try {
      const parseTime = (timeStr: string): Date => {
        const today = new Date()
        today.setSeconds(0, 0)
        const [hours, minutes] = timeStr.split(':').map(Number)
        today.setHours(hours, minutes || 0)
        return today
      }

      const actual = parseTime(actualOutTime)
      const standard = parseTime(standardOutTime)

      if (actual < standard) {
        const diffMs = standard.getTime() - actual.getTime()
        const hours = Math.floor(diffMs / (1000 * 60 * 60))
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

        if (hours > 0 || minutes > 0) {
          return `${hours}:${minutes.toString().padStart(2, '0')}`
        }
      }

      return ''
    } catch (error) {
      console.error('Error calculating early by:', error)
      return ''
    }
  }

  // Function to calculate overtime based on duration and standard working hours
  const calculateOvertime = (duration: string, standardHours: number = 10): string => {
    if (!duration) return ''

    try {
      const [hours, minutes] = duration.split(':').map(Number)
      const totalHours = hours + (minutes / 60)

      if (totalHours > standardHours) {
        const overtimeHours = totalHours - standardHours
        const otHours = Math.floor(overtimeHours)
        const otMinutes = Math.round((overtimeHours - otHours) * 60)

        return `${otHours}:${otMinutes.toString().padStart(2, '0')}`
      }

      return ''
    } catch (error) {
      console.error('Error calculating overtime:', error)
      return ''
    }
  }

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target
    let updatedFormData = { ...formData, [name]: value }

    // Auto-fill Out Time when In Time is selected (add 10 hours)
    if (name === 'inTime' && value) {
      const inTime = new Date(`1970-01-01T${value}:00`)
      const outTime = new Date(inTime.getTime() + (10 * 60 * 60 * 1000)) // Add 10 hours
      const outTimeString = outTime.toTimeString().slice(0, 5) // Format HH:MM
      updatedFormData.outTime = outTimeString
    }

    // Auto-calculate Duration, Late By, Early By, and Overtime when In Time or Out Time changes
    if ((name === 'inTime' || name === 'outTime') && updatedFormData.inTime && updatedFormData.outTime) {
      // Calculate duration
      const duration = calculateDuration(updatedFormData.inTime, updatedFormData.outTime)
      updatedFormData.duration = duration

      // Calculate late by using general settings
      // const lateBy = calculateLateBy(updatedFormData.inTime, generalSettings.inTime)
      // updatedFormData.lateBy = lateBy
      const lateBy = shift
      ? calculateLateBy(updatedFormData.inTime, shift.startTime)
      : '';

      // Calculate early by using general settings
      //   const earlyBy = calculateEarlyBy(updatedFormData.outTime, generalSettings.outTime)
      // updatedFormData.earlyBy = earlyBy
      const earlyBy = shift ? calculateEarlyBy(updatedFormData.outTime, shift.endTime) : '';

      // Calculate standard working hours from general settings
      const standardWorkingHours = calculateDuration(shift?.startTime || '09:00', shift?.endTime || '19:00')
      const [standardHours] = standardWorkingHours.split(':').map(Number)


      // Calculate overtime based on standard working hours
      const overtime = calculateOvertime(duration, standardHours || 10)
      updatedFormData.overtime = overtime
    }

    setFormData(updatedFormData)

    // Clear error for this field
    if (inputError[name]) {
      setInputError(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleSelectChange = (name: string, value: string | null) => {
    setFormData(prev => ({ ...prev, [name]: value || '' }))

    // Clear error for this field
    if (inputError[name]) {
      setInputError(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const errors: InputErrorType = {}

    if (!formData.employeeCode.trim()) {
      errors.employeeCode = 'Employee is required'
    }

    if (!formData.date.trim()) {
      errors.date = 'Date is required'
    }

    if (!formData.inTime.trim()) {
      errors.inTime = 'In time is required'
    }

    if (formData.inTime && formData.outTime) {
      const inTime = new Date(`1970-01-01T${formData.inTime}:00`)
      const outTime = new Date(`1970-01-01T${formData.outTime}:00`)

      // if (outTime <= inTime) {
      //   errors.outTime = 'Out time must be after in time'
      // }
      if (outTime < inTime) {
        outTime.setDate(outTime.getDate() + 1)
      }
    }
    setInputError(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    setLoading(true)

    try {
      if (modalState.mode === 'create') {
        const result = await createAttendanceRecord(formData)

        if (result.statusCode === 200 || result.statusCode === 201) {
          toast.success(result.message)

          // NOTE: Audit logging is handled by the backend with the correct employee code
          // Frontend logging was removed to prevent duplicate logs with incorrect employee IDs

          if (result?.data) {
            onCreated?.(result.data)
          }
          onSuccess()
          onClose()
        } else {
          toast.error(result.message || 'Failed to create attendance record')
        }
      } else if (modalState.mode === 'edit' && modalState.record) {
        const updateData: UpdateAttendanceFormData = {
          inTime: formData.inTime,
          outTime: formData.outTime,
          duration: formData.duration,
          lateBy: formData.lateBy,
          earlyBy: formData.earlyBy,
          overtime: formData.overtime
        }

        const result = await updateAttendanceRecord(
          modalState.record.employeeCode,
          formData.date,
          updateData
        )

        if (result.statusCode === 200) {
          toast.success(result.message)

          // NOTE: Audit logging is handled by the backend with the correct employee code

          onSuccess()
          onClose()
        } else {
          toast.error(result.message || 'Failed to update attendance record')
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!modalState.record) return

    setLoading(true)

    try {
      const result = await deleteAttendanceRecord(
        modalState.record.employeeCode,
        modalState.record.date.split('T')[0]
      )

      if (result.statusCode === 200) {
        toast.success(result.message)

        // NOTE: Audit logging is handled by the backend with the correct employee code

        onSuccess()
        onClose()
      } else {
        toast.error(result.message || 'Failed to delete attendance record')
      }
    } catch (error: any) {
      toast.error(error.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const getModalTitle = () => {
    switch (modalState.mode) {
      case 'create': return 'Create Attendance Record'
      case 'edit': return 'Edit Attendance Record'
      case 'view': return 'View Attendance Record'
      case 'delete': return 'Delete Attendance Record'
      default: return 'Attendance Record'
    }
  }

  return (
    <Modal
      show={modalState.isOpen}
      onHide={onClose}
      backdrop="static"
      centered
      size={modalState.mode === 'view' ? 'lg' : 'xl'}
    >
      <Modal.Header closeButton style={{
        borderBottom: '1px solid #E2ECF7',
        padding: '16px 24px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
          }}>
            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="calendar-plus" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style={{ fontSize: '18px', width: '18px', height: '18px', color: '#FFFFFF' }}>
              <path fill="currentColor" d="M96 32V64H48C21.49 64 0 85.49 0 112v48H448V112c0-26.51-21.49-48-48-48H352V32c0-17.67-14.33-32-32-32s-32 14.33-32 32V64H160V32c0-17.67-14.33-32-32-32S96 14.33 96 32zM448 192H0V464c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V192zm-64 64c0 8.84-7.16 16-16 16h-64c-8.84 0-16-7.16-16-16s7.16-16 16-16h64c8.84 0 16 7.16 16 16zm0 96c0 8.84-7.16 16-16 16h-64c-8.84 0-16-7.16-16-16s7.16-16 16-16h64c8.84 0 16 7.16 16 16z"></path>
            </svg>
          </div>
          <Modal.Title style={{
            color: '#123B73',
            fontWeight: '600',
            fontSize: '18px',
            marginBottom: '0'
          }}>{getModalTitle()}</Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: '24px' }}>
        {modalState.mode === 'view' && viewRecord ? (
          // View Mode - Display only
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="fw-bold" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Employee Code:</label>
              <p className="mb-1" style={{ color: '#64748B', fontSize: '13px' }}>{viewRecord.employeeCode}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="fw-bold" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Date:</label>
              <p className="mb-1" style={{ color: '#64748B', fontSize: '13px' }}>{new Date(viewRecord.date).toLocaleDateString()}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="fw-bold" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>In Time:</label>
              <p className="mb-1" style={{ color: '#64748B', fontSize: '13px' }}>{viewRecord.inTime || 'N/A'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="fw-bold" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Out Time:</label>
              <p className="mb-1" style={{ color: '#64748B', fontSize: '13px' }}>{viewRecord.outTime || 'N/A'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="fw-bold" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Duration:</label>
              <p className="mb-1" style={{ color: '#64748B', fontSize: '13px' }}>{viewRecord.duration || 'N/A'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="fw-bold" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Overtime:</label>
              <p className="mb-1" style={{ color: '#64748B', fontSize: '13px' }}>{viewRecord.overtime || 'N/A'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="fw-bold" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Late By:</label>
              <p className="mb-1" style={{ color: '#64748B', fontSize: '13px' }}>{viewRecord.lateBy || 'N/A'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="fw-bold" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Early By:</label>
              <p className="mb-1" style={{ color: '#64748B', fontSize: '13px' }}>{viewRecord.earlyBy || 'N/A'}</p>
            </div>
            <div className="col-12 mb-3">
              <label className="fw-bold" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Punch Records:</label>
              <p className="mb-1" style={{ color: '#64748B', fontSize: '13px' }}>{viewRecord.punchRecords || 'N/A'}</p>
            </div>
          </div>
        ) : modalState.mode === 'delete' && modalState.record ? (
          // Delete Mode - Confirmation
          <div className="text-center py-4">
            <div className="mb-4">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#fef2f2',
                  border: '3px solid #fecaca'
                }}
              >
                <i
                  className="fa fa-trash-alt"
                  style={{
                    fontSize: '32px',
                    color: '#dc3545'
                  }}
                ></i>
              </div>
            </div>

            <h4 className="fw-bold text-dark mb-3">Delete Attendance Record</h4>

            <div className="alert alert-danger d-inline-block mx-auto mb-4" style={{ maxWidth: '400px' }}>
              <div className="d-flex align-items-center">
                <i className="fa fa-exclamation-triangle me-2" style={{ color: '#721c24' }}></i>
                <span className="fw-semibold" style={{ color: '#721c24' }}>
                  This action cannot be undone
                </span>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-muted mb-3" style={{ fontSize: '15px' }}>
                You are about to permanently delete the attendance record for:
              </p>

              <div className="card border-0 bg-light mx-auto" style={{ maxWidth: '350px' }}>
                <div className="card-body py-3">
                  <div className="row align-items-center">
                    <div className="col-4">
                      <i className="fa fa-user text-primary me-2"></i>
                      <span className="small text-muted">Employee:</span>
                    </div>
                    <div className="col-8">
                      <span className="fw-bold text-primary">{modalState.record.employeeCode}</span>
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="row align-items-center">
                    <div className="col-4">
                      <i className="fa fa-calendar text-primary me-2"></i>
                      <span className="small text-muted">Date:</span>
                    </div>
                    <div className="col-8">
                      <span className="fw-bold text-primary">
                        {new Date(modalState.record.date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="row align-items-center">
                    <div className="col-4">
                      <i className="fa fa-clock text-primary me-2"></i>
                      <span className="small text-muted">Times:</span>
                    </div>
                    <div className="col-8">
                      <span className="fw-bold text-primary">
                        {modalState.record.inTime || 'N/A'} - {modalState.record.outTime || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-muted small mb-0" style={{ fontSize: '13px' }}>
              Make sure this is the correct record before proceeding.
            </p>
          </div>
        ) : (
          // Create/Edit Mode - Form
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="required fw-bold form-label" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Employee</Form.Label>
                  {(modalState.mode === 'edit') ? (
                    <div className="form-control" style={{ backgroundColor: '#f5f5f5', padding: '0.5rem 0.75rem', border: '1px solid #E2ECF7', borderRadius: '4px', fontSize: '13px' }}>
                      {formData.employeeCode}
                    </div>
                  ) : (
                    <Select2
                      data={employees}
                      value={formData.employeeCode}
                      onChange={(value) => handleSelectChange('employeeCode', value)}
                      placeholder="Select employee..."
                    />
                  )}
                  <InputErrorBox Message={inputError.employeeCode} />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="required fw-bold form-label" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    disabled={modalState.mode === 'edit'}
                    style={{ border: '1px solid #E2ECF7', borderRadius: '4px', padding: '4px 8px', fontSize: '11px' }}
                  />
                  <InputErrorBox Message={inputError.date} />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold form-label" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>In Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="inTime"
                    value={formData.inTime}
                    onChange={handleInputChange}
                    style={{ border: '1px solid #E2ECF7', borderRadius: '4px', padding: '4px 8px', fontSize: '11px' }}
                  />
                  <InputErrorBox Message={inputError.inTime} />
                  <small className="text-muted" style={{ fontSize: '11px', color: '#64748B' }}>
                    <i className="fa fa-info-circle me-1"></i>
                    Out time will auto-fill with 10-hour gap
                  </small>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold form-label" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Out Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="outTime"
                    value={formData.outTime}
                    onChange={handleInputChange}
                    style={{ border: '1px solid #E2ECF7', borderRadius: '4px', padding: '4px 8px', fontSize: '11px' }}
                  />
                  <InputErrorBox Message={inputError.outTime} />
                  <small className="text-muted" style={{ fontSize: '11px', color: '#64748B' }}>
                    <i className="fa fa-database me-1"></i>
                    Data stored in SQL table with all required columns
                  </small>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold form-label" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Duration</Form.Label>
                  <Form.Control
                    type="text"
                    name="duration"
                    value={formData.duration}
                    readOnly
                    className="bg-light"
                    placeholder="Auto-calculated"
                    style={{ border: '1px solid #E2ECF7', borderRadius: '4px', padding: '4px 8px', fontSize: '11px' }}
                  />
                  <small className="text-muted" style={{ fontSize: '11px', color: '#64748B' }}>
                    <i className="fa fa-calculator me-1"></i>
                    Auto-calculated from In Time and Out Time
                  </small>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold form-label" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Overtime</Form.Label>
                  <Form.Control
                    type="text"
                    name="overtime"
                    value={formData.overtime || 'No overtime'}
                    readOnly
                    className="bg-light"
                    placeholder="Auto-calculated"
                    style={{ border: '1px solid #E2ECF7', borderRadius: '4px', padding: '4px 8px', fontSize: '11px' }}
                  />
                  <small className="text-muted" style={{ fontSize: '11px', color: '#64748B' }}>
                    <i className="fa fa-clock me-1"></i>
                    Based on {shift?.startTime || '--'} - {shift?.endTime || '--'} working hours
                  </small>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold form-label" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Late By</Form.Label>
                  <Form.Control
                    type="text"
                    name="lateBy"
                    value={formData.lateBy || 'On time'}
                    readOnly
                    className="bg-light"
                    placeholder="Auto-calculated"
                    style={{ border: '1px solid #E2ECF7', borderRadius: '4px', padding: '4px 8px', fontSize: '11px' }}
                  />
                  <small className="text-muted" style={{ fontSize: '11px', color: '#64748B' }}>
                    <i className="fa fa-info-circle me-1"></i>
                    Based on {shift?.startTime || '--'} standard start time
                  </small>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold form-label" style={{ color: '#123B73', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Early By</Form.Label>
                  <Form.Control
                    type="text"
                    name="earlyBy"
                    value={formData.earlyBy || 'Full day'}
                    readOnly
                    className="bg-light"
                    placeholder="Auto-calculated"
                    style={{ border: '1px solid #E2ECF7', borderRadius: '4px', padding: '4px 8px', fontSize: '11px' }}
                  />
                  <small className="text-muted" style={{ fontSize: '11px', color: '#64748B' }}>
                    <i className="fa fa-info-circle me-1"></i>
                    Based on {shift?.endTime || '--'} standard end time
                  </small>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0" style={{ padding: '0 24px 24px' }}>
        <div className="w-100 d-flex justify-content-center gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            size="md"
            className="px-4"
            style={{
              backgroundColor: '#FFFFFF',
              color: '#64748B',
              border: '1px solid #E2ECF7',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            {modalState.mode === 'view' ? 'Close' : 'Cancel'}
          </Button>

          {modalState.mode === 'delete' && (
            <Button
              color="red"
              onClick={handleDelete}
              loading={loading}
              size="md"
              className="px-4"
              leftSection={<i className="fa fa-trash-alt me-1"></i>}
              style={{
                backgroundColor: '#EF4444',
                color: '#FFFFFF',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              {loading ? 'Deleting...' : 'Delete Record'}
            </Button>
          )}

          {(modalState.mode === 'create' || modalState.mode === 'edit') && (
            <Button
              onClick={handleSubmit}
              loading={loading}
              size="md"
              className="px-4"
              leftSection={
                modalState.mode === 'create' ?
                  <i className="fa fa-plus me-1"></i> :
                  <i className="fa fa-save me-1"></i>
              }
              style={{
                backgroundColor: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              {loading ?
                (modalState.mode === 'create' ? 'Creating...' : 'Updating...') :
                (modalState.mode === 'create' ? 'Create Record' : 'Update Record')
              }
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default AttendanceCRUDModal
